import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  marketingButton,
  marketingFields,
  marketingType,
} from "../../marketing.type";
import { useForm } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import { FormField } from "@themes/pollinator/ui/FormField";
import { Type } from "@themes/pollinator/ui/Type";
import { Button } from "@themes/pollinator/ui/Button";
import { Loader } from "@themes/pollinator/ui/Loader";
import { FieldError } from "@themes/pollinator/ui/FieldError";
import type { OptionType } from "@themes/pollinator/ui/Select";
import { MarketingIntakeLayout } from "../../components/IntakeLayout";
import { PublicQuizQuestion, componentPropsFor } from "@platform/features/programQuiz";
import {
  buildIntakeSchema,
  drawnQuestions,
  emptyAnswers as emptyAnswersFor,
  type IntakeConfirmation,
  type IntakeFieldName,
  type IntakeFormData,
  type IntakeQuestion,
  type IntakeSubmissionResult,
} from "../IntakeForm";
import { useIntakeQuizTracking } from "../../marketing.analytics";
import { useBreakpoint } from "@core/hooks/useBreakpoint";
import { fieldNamesForStep, planSteps } from "./intakeWizard.steps";
import type { BookingDay, BookingSlot } from "../../components/BookingFlow";
import { withLinks } from "../../utils/withLinks";

export type Props = {
  formSlug: string;
  /** Copy for the panel beside the form */
  audienceLabel?: string;
  headline?: string;
  introBody?: string[];
  stepIntro?: string;
  /** The wording on the button that finishes the form */
  submitLabel?: string;
  /** What the form asks for */
  requiredFields: IntakeFieldName[];
  optionalFields: IntakeFieldName[];
  fieldLabels?: Record<string, string>;
  extraFields?: IntakeQuestion[];
  /** The form's id and its quiz's, recorded with every measurement event */
  formId?: number;
  quizId?: number;
  /** Booking */
  booksAMeeting?: boolean;
  bookingStepStyle?: string;
  days?: BookingDay[];
  isLoadingTimes?: boolean;
  /** Set when availability could not be read at all */
  bookingUnavailable?: boolean;
  schedulingUrl?: string;
  selectedTime?: string;
  onSelectTime?: (startTime: string) => void;
  /** Organizations matching what has been typed into the organization select */
  organizationOptions?: OptionType[];
  /** The fixed list the role select offers */
  roleOptions?: OptionType[];
  isLoadingRoles?: boolean;
  /** Told what is being typed into the organization select */
  onOrganizationSearch?: (query: string) => void;
  /** Where Cancel and the logo lead */
  homeHref?: string;
  /**
   * How long after arriving at the last screen its button will not send, so
   * that a double press on Continue cannot send it. Tests set this to nothing.
   */
  sendGuardMs?: number;
  /** Shown instead of the form once everything succeeds */
  confirmation?: IntakeConfirmation;
  /** Why a submission failed, when it did */
  errorReason?: string;
  onSubmit: (data: IntakeFormData) => Promise<IntakeSubmissionResult | void>;
  className?: string;
};

/** How many days or sessions are offered at once; the design draws five. */
const BOOKING_OPTIONS_SHOWN = 5;

/** A double press is two presses within about this long of each other. */
const SEND_GUARD_MS = 500;

const ERROR_COPY: Record<string, { heading: string; body: string }> = {
  slot_taken: {
    heading: "That time is no longer available.",
    body: "Please choose another time or book directly through Calendly.",
  },
  booking_unavailable: {
    heading: "We cannot book that for you right now.",
    body: "Please book directly through Calendly and we will see you there.",
  },
};

const DEFAULT_ERROR = {
  heading: "Something went wrong.",
  body: "Please try again, or book directly through Calendly.",
};

/** The same, for a form that books nothing and so has no Calendly to offer. */
const DEFAULT_ERROR_NO_BOOKING = {
  heading: "Something went wrong.",
  body: "Your answers were not sent. Please try again in a moment.",
};

/**
 * Failures that trying again in a moment will not mend, whatever the form.
 * Each says what happened, so the visitor is not left retrying something
 * that cannot succeed.
 */
const LASTING_ERROR_COPY: Record<string, { heading: string; body: string }> = {
  // The form was unpublished while it was open in this tab.
  "Unknown form": {
    heading: "This form is no longer available.",
    body: "It was closed while you were filling it in, so your answers were not sent.",
  },
  "Missing required fields": {
    heading: "Some answers are missing.",
    body: "Your answers were not sent. Please go back and fill in every required field.",
  },
  "Invalid email": {
    heading: "That email address does not look right.",
    body: "Your answers were not sent. Please go back and check your email address.",
  },
  "Invalid answer": {
    heading: "One of the answers could not be accepted.",
    body: "Your answers were not sent. Please go back and check them.",
  },
};

const DEFAULT_CONFIRMATION: IntakeConfirmation = {
  heading: "Thank you.",
  body: "We have your details and will be in touch.",
};

/** What can take focus within a field: its input, or one of its options. */
const FOCUSABLE = 'input:not([type="hidden"]), textarea, select, button, [tabindex="0"]';

/**
 * Whether the page is narrow enough that the argument and the form are two
 * screens, shown one after the other, and not two panels side by side.
 */
function isIntroItsOwnScreen() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    !window.matchMedia("(min-width: 1024px)").matches
  );
}

/** "10:00am EST": the design's way of writing a time, timezone included. */
function formatTime(iso: string) {
  const when = new Date(iso);
  const time = when
    .toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    .replace(" ", "")
    .toLowerCase();
  const zone = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" })
    .formatToParts(when)
    .find((part) => part.type === "timeZoneName")?.value;
  return zone ? `${time} ${zone}` : time;
}

/** "Wednesday, 12/10/2026" */
function formatDay(iso: string) {
  return new Date(iso).toLocaleDateString("en-US", {
    weekday: "long",
    day: "numeric",
    month: "numeric",
    year: "numeric",
  });
}

/** The radio the design draws at the head of each option row. */
function RadioMark({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
        selected ? "bg-brand-800" : "border border-brand-800"
      }`}
    >
      {selected && <span className="h-2 w-2 rounded-full bg-white" />}
    </span>
  );
}

/**
 * An option row as the design draws it: 56px, left-set behind a radio, and
 * when chosen a thicker green border rather than an inverted fill. A short
 * phone screen draws it 48px, still a comfortable press.
 */
const optionRowClass = (selected: boolean) =>
  `h-14 max-lg:[@media(max-height:740px)]:h-12 w-full justify-start px-4 rounded-full bg-brand-50 hover:bg-brand-50 ${
    selected ? "border-2 border-brand-600" : "border border-brand-700"
  }`;
const OPTION_TYPE =
  "flex items-center gap-3 px-0 text-[16px] md:text-[16px] leading-6 font-medium text-brand-700";

/** What a booking step says when Continue is pressed with nothing chosen. */
const CHOICE_REQUIRED: Record<string, string> = {
  day: "Choose a day to continue.",
  time: "Choose a time to continue.",
  sessions: "Choose a session to continue.",
};

/**
 * The contact screen is drawn in groups: fields 20px apart inside a group,
 * and the groups 24px apart, closing up on a shorter desktop window or a
 * short phone screen.
 */
const FIELD_GROUP = `flex flex-col ${marketingFields.gap}`;
const FIELD_GROUP_SHORT_WINDOW =
  "lg:[@media(max-height:900px)]:gap-2.5 lg:[@media(max-height:800px)]:gap-1 max-lg:[@media(max-height:740px)]:gap-4";
const FIELD_GROUP_GAP = `gap-6 ${FIELD_GROUP_SHORT_WINDOW}`;
/** The longest form, which answers a question in a box on this screen, is drawn closer. */
const FIELD_GROUP_GAP_CLOSE = `gap-6 lg:gap-4 ${FIELD_GROUP_SHORT_WINDOW}`;

/** A label sits 6px above a field, and 12px above the box a question is answered in. */
const FIELD = `${marketingFields.noMark} gap-1.5`;
const QUESTION_FIELD = `${marketingFields.greenMark} gap-1.5`;
const QUESTION_BOX = `${marketingFields.greenMark} gap-3`;

/**
 * A dropdown inside the form column, set the way the design sets one: 40px
 * tall with the form's 16px type and 8px corners, no shadow, and a green
 * chevron. The inner text input is exempted from the box the form column
 * draws around every input, since the select draws its own.
 */
const SELECT_BOX = [
  "[&_input]:!h-auto [&_input]:!px-0 [&_input]:!border-0",
  "[&_.drop-shadow-lg]:drop-shadow-none",
  "[&_.h-10]:rounded-[8px] [&_.h-10]:py-2 [&_.h-10]:text-[16px] [&_.h-10]:leading-6 [&_.h-10]:shadow-none",
  "[&_.h-10_svg]:h-4 [&_.h-10_svg]:w-4 [&_.h-10_svg]:text-brand-600",
  "lg:[@media(max-height:900px)]:[&_.h-10]:h-9 lg:[@media(max-height:800px)]:[&_.h-10]:h-8",
].join(" ");

/**
 * A screen of questions, where it differs from the contact screen. Several
 * questions take the panel's full width, which keeps the longest of them on
 * one line. The rows of a pick-several question are drawn flat, in the lighter
 * green and 12px apart, with a chosen row keeping its own thicker border, and
 * the box a question is answered in is taller than the contact screen's.
 */
const QUESTION_SCREEN = [
  "[&_.shadow-md]:shadow-none hover:[&_.shadow-md]:shadow-none",
  "[&_.shadow-md:not(.border-2)]:border-brand-700 [&_.shadow-md_.text-brand-800]:text-brand-700",
  "[&_.flex-col.gap-4]:gap-3 [&_.shadow-md]:py-[15px]",
  "lg:[&_textarea]:min-h-[122px]",
  // The pills an answer is picked from: 24px tall, 6px apart, flush with the
  // labels, in medium weight, and with no room held for a tick until one is
  // chosen, so that a label sits in the middle of its pill.
  "[&_div[role=checkbox].rounded-full]:px-4 [&_div[role=checkbox].rounded-full]:py-px",
  "[&_div[role=checkbox].rounded-full_span]:!font-medium",
  "[&_div[role=checkbox]>.opacity-0]:hidden",
  "[&_.gap-x-2]:gap-x-1.5 [&_.gap-x-2.p-1]:p-0",
].join(" ");
const WIDE_QUESTION_SCREEN = "lg:max-w-[496px]";
/** How many questions a screen holds before it is given the full width. */
const WIDE_SCREEN_QUESTIONS = 3;

/**
 * How far a question's label sits above its answer: 12px above a box or a
 * stack of rows, 6px above a field, a dropdown or a row of pills.
 */
function questionClassFor(field: IntakeQuestion) {
  const { component } = componentPropsFor(field);
  if (component === "Textarea" || component === "RadioGroup") return QUESTION_BOX;
  if (component === "Select") return `${QUESTION_FIELD} ${SELECT_BOX}`;
  return QUESTION_FIELD;
}

/** The id the shared affiliation row gives its organization input. */
const ORGANIZATION_INPUT_ID = "affiliations.0.organization";

/**
 * The organization and role selects, set the way the design sets them here:
 * side by side from a tablet up and 20px apart, 40px tall with the form's
 * 16px type and 8px corners, no shadow, and a green chevron. The inner text
 * input is exempted from the box the form column draws around every input,
 * since the select draws its own.
 */
const AFFILIATION_FIELD = [
  "p-0",
  "[&_[data-testid=affiliation-row]]:gap-5",
  "[&_[data-testid=affiliation-row]>div]:gap-1.5 [&_[data-testid=affiliation-row]>div]:min-w-0",
  SELECT_BOX,
].join(" ");

/**
 * One intake form, answered a screen at a time.
 *
 * Every audience gets the same two panels and the same progress counter; what
 * differs is how many screens there are and what is on them, which is
 * configuration rather than code. A participant answers one screen, an
 * organization three, a funder five.
 *
 * All the screens share a single form and a single schema, and only the fields
 * on the current screen are validated before moving on. That keeps the
 * submission one object, the way the server expects it, while letting somebody
 * be told about a mistake on the screen where they made it.
 */
export function MarketingIntakeWizard({
  formSlug,
  audienceLabel,
  headline,
  introBody,
  stepIntro,
  submitLabel,
  requiredFields,
  optionalFields,
  fieldLabels,
  extraFields,
  formId,
  quizId,
  booksAMeeting,
  bookingStepStyle,
  days,
  isLoadingTimes,
  bookingUnavailable,
  schedulingUrl,
  selectedTime,
  onSelectTime,
  organizationOptions,
  roleOptions,
  isLoadingRoles,
  onOrganizationSearch,
  homeHref = "/",
  sendGuardMs = SEND_GUARD_MS,
  confirmation,
  errorReason,
  onSubmit,
  className,
}: Props) {
  const [index, setIndex] = useState(0);
  // When the screen now showing was arrived at from another.
  const arrivedAt = useRef(0);
  // Set when Continue is pressed on a booking step with nothing chosen.
  const [isChoiceMissing, setIsChoiceMissing] = useState(false);
  // On a phone the argument is a screen of its own, shown first.
  const [isIntroShown, setIsIntroShown] = useState(true);
  // Each screen is an entry in the browser's history, the phone's argument
  // included, so that the browser's Back goes to the screen before and not
  // out of the form, taking every answer with it. The entry says which
  // screen it is. Going forward through history is followed only as far as
  // this visit has got: an entry left over from before a reload names a
  // screen whose earlier screens have not been answered this time.
  const furthest = useRef(0);
  const stepTotal = useRef(0);
  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      const step = (event.state as { intake?: { step?: number } } | null)?.intake?.step;
      if (typeof step !== "number") {
        setIndex(0);
        setIsIntroShown(true);
        return;
      }
      if (step > furthest.current || step >= stepTotal.current) return;
      setIsChoiceMissing(false);
      setIsIntroShown(false);
      setIndex(step);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const leaveIntro = () => {
    window.history.pushState({ intake: { step: 0 } }, "");
    setIsIntroShown(false);
    window.scrollTo(0, 0);
  };
  // Where focus is put when a screen is arrived at: the top of the screen,
  // so a keyboard or a screen reader starts there and not on the button that
  // was just pressed.
  const screenRef = useRef<HTMLDivElement>(null);
  const hasMoved = useRef(false);
  useEffect(() => {
    if (!hasMoved.current) return;
    screenRef.current?.focus({ preventScroll: true });
  }, [index]);
  const [selectedDate, setSelectedDate] = useState<string>();
  const started = useRef(false);
  const track = useIntakeQuizTracking({ quizId, formId, formSlug });

  const questions = useMemo(() => extraFields ?? [], [extraFields]);
  // A phone is given the contact screen in two, since the whole of it is
  // taller than a phone shows; the two panels side by side ask it at once.
  const splitContact = !useBreakpoint("lg");
  const { steps, finalLabel } = useMemo(
    () =>
      planSteps({
        requiredFields,
        optionalFields,
        extraFields: questions,
        booksAMeeting: !!booksAMeeting,
        bookingStepStyle,
        finalLabel: submitLabel,
        fieldLabels,
        splitContact,
      }),
    [requiredFields, optionalFields, questions, booksAMeeting, bookingStepStyle, submitLabel, fieldLabels, splitContact]
  );
  // A screen crossing that width part way through, a tablet turned on its
  // side, keeps somebody on the screen they were on: past the contact screen,
  // the screens after it move up or down one.
  const [planWasSplit, setPlanWasSplit] = useState(splitContact);
  if (planWasSplit !== splitContact) {
    setPlanWasSplit(splitContact);
    const contactAt = steps.findIndex((planned) => planned.kind === "contact");
    const contactScreens = steps.filter((planned) => planned.kind === "contact").length;
    if (contactAt >= 0 && index > contactAt) {
      const moved = splitContact ? index + (contactScreens - 1) : index - 1;
      setIndex(Math.min(Math.max(contactAt, moved), steps.length - 1));
    }
  }

  const usesAffiliation = steps.some(
    (planned) => planned.kind === "contact" && planned.affiliation
  );
  const schema = useMemo(
    () =>
      buildIntakeSchema(requiredFields, questions, {
        affiliation: usesAffiliation,
      }),
    [requiredFields, questions, usesAffiliation]
  );

  const emptyAnswers = useMemo(() => emptyAnswersFor(questions), [questions]);

  const { form, handleSubmit } = useForm({
    schema,
    onSubmit: async (data: IntakeFormData) => {
      const result = await onSubmit(data);
      track.completed(result?.quizResponseId);
    },
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      confirmEmail: "",
      phoneNumber: "",
      confirmPhoneNumber: "",
      organizationName: "",
      selfReportedRole: "",
      message: "",
      affiliations: [],
      answers: emptyAnswers,
    },
  });

  // Each of these is recorded once for what it describes, however often the
  // effect behind it runs, so that a form opened is not counted twice.
  const viewed = useRef<string>();
  useEffect(() => {
    if (viewed.current === formSlug) return;
    viewed.current = formSlug;
    track.loaded();
  }, [track, formSlug]);

  // Starting means doing something: typing into a field or choosing a time.
  // The form also changes its own values, when it sets its defaults and when
  // it marks a screen's fields as touched, and those are not somebody
  // starting. Counting them recorded everyone who opened a form as having
  // started it.
  const markStarted = useCallback(() => {
    if (started.current) return;
    started.current = true;
    track.started();
  }, [track]);

  useEffect(() => {
    const subscription = form.watch((_values, change) => {
      if (change.type === "change") markStarted();
      // The two addresses are checked against each other, and the complaint
      // is made under the second. Changing the first after they matched left
      // them different with nothing said, and a button that looked disabled
      // for no visible reason.
      if (change.name === "email" && form.getValues("confirmEmail" as never)) {
        void form.trigger("confirmEmail" as never);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, markStarted]);

  // A question waiting on another answer, drawn disabled, lets go of whatever
  // it held: an answer given before the answer it depends on changed would
  // otherwise sit there, greyed out, as if it still counted.
  const currentAnswers = form.watch("answers");
  useEffect(() => {
    drawnQuestions(questions, currentAnswers).forEach(({ question, isWaiting }) => {
      if (!isWaiting) return;
      const name = `answers.${question.name}` as never;
      const value = form.getValues(name) as unknown;
      if (Array.isArray(value) ? value.length > 0 : !!value) {
        form.setValue(name, (Array.isArray(value) ? [] : "") as never);
      }
    });
  }, [questions, currentAnswers, form]);

  const step = steps[index];
  const isLast = index === steps.length - 1;
  stepTotal.current = steps.length;

  // Each screen reached is recorded, the first included, so the report can
  // say where in a form people stop. Going back to a screen records it again:
  // the report counts people, and how often they returned is worth knowing.
  const stepKind = step.kind;
  const stepCount = steps.length;
  const reachedStep = useRef<string>();
  useEffect(() => {
    // On a phone the argument is shown first, as a screen of its own, and
    // the form's first screen has not been reached until that is left.
    // Counting it sooner said everybody who read the argument had reached
    // the day or the contact screen.
    if (isIntroShown && isIntroItsOwnScreen()) return;
    const reached = `${formSlug}:${index}`;
    if (reachedStep.current === reached) return;
    reachedStep.current = reached;
    track.step({ index: index + 1, count: stepCount, kind: stepKind });
  }, [track, formSlug, index, stepCount, stepKind, isIntroShown]);

  // A submission that failed is recorded with why. A slot taken by somebody
  // else and a booking Calendly refused look the same to the visitor's
  // patience and very different to whoever has to fix it.
  useEffect(() => {
    if (errorReason) track.failed(errorReason);
  }, [track, errorReason]);

  // A booking screen that has nothing to offer is recorded once somebody
  // reaches it, with why: the times could not be read, or none were free.
  // Without this a calendar that had broken looked, in the numbers, like
  // visitors losing interest on the first screen.
  const isBookingStep = stepKind === "day" || stepKind === "sessions" || stepKind === "time";
  const bookingProblem = !isBookingStep
    ? undefined
    : bookingUnavailable
      ? "availability_failed"
      : !isLoadingTimes && stepKind !== "time" && (days ?? []).length === 0
        ? "nothing_available"
        : undefined;
  const reportedProblems = useRef(new Set<string>());
  useEffect(() => {
    if (!bookingProblem || (isIntroShown && isIntroItsOwnScreen())) return;
    if (reportedProblems.current.has(bookingProblem)) return;
    reportedProblems.current.add(bookingProblem);
    track.problem(bookingProblem, { stepKind });
  }, [track, bookingProblem, stepKind, isIntroShown]);

  // The button for a screen looks disabled until that screen is answered.
  // The form's own validity covers every screen at once, so this screen's
  // fields are picked out of a parse of the current values: reading the
  // errors instead would only know about fields somebody has already touched.
  const stepFieldNames = fieldNamesForStep(step);
  const parsed = schema.safeParse(form.watch());
  const hasStepIssue =
    !parsed.success &&
    parsed.error.issues.some((issue) =>
      stepFieldNames.some(
        (name) =>
          issue.path.join(".") === name ||
          issue.path.join(".").startsWith(`${name}.`)
      )
    );
  const isChoiceMade =
    step.kind === "day"
      ? !!selectedDate
      : step.kind === "time" || step.kind === "sessions"
        ? !!selectedTime
        : true;
  const labelFor = (field: IntakeFieldName, fallback: string) =>
    fieldLabels?.[field] ?? fallback;
  const placeholderFor = (field: IntakeFieldName, fallback: string) =>
    fieldLabels?.[field] ? undefined : fallback;
  const isRequired = (field: IntakeFieldName) => requiredFields.includes(field);

  const calendlyButton = schedulingUrl ? (
    <Button
      as="a"
      href={schedulingUrl}
      target="_blank"
      rel="noopener noreferrer"
      variant="secondary"
      data-testid="booking-open-calendly"
    >
      Open Calendly
    </Button>
  ) : null;

  if (confirmation || form.formState.isSubmitSuccessful) {
    const { heading, body } = confirmation ?? DEFAULT_CONFIRMATION;
    return (
      <MarketingIntakeLayout
        audienceLabel={audienceLabel}
        headline={headline}
        introBody={introBody}
        // The line above the form tells somebody what to do on it. Once it
        // is done the panel holds the confirmation alone.
        mobileView="form"
        homeHref={homeHref}
        className={className}
      >
        {/* A phone sets the confirmation 32px in, a little inside the fields
            it replaces, with its heading and copy 24px apart. */}
        <div
          className="flex flex-col gap-6 px-1.5 md:gap-8 lg:px-0 w-full max-w-[480px] mx-auto"
          data-testid="intake-confirmation"
        >
          <Type
            variant="h3"
            as="h2"
            // Drawn in a box trimmed to its capitals, so the gap under it is
            // measured from the letters. A browser without the property
            // leaves the line box whole and the copy a few pixels lower.
            className="text-[36px] leading-[45px] tracking-[-0.72px] md:text-[36px] md:leading-[45px] font-medium [text-box:trim-both_cap_alphabetic]"
          >
            {heading}
          </Type>
          <div className="flex flex-col gap-4">
            {body
              .split("\n")
              .filter((line) => line.trim())
              .map((line) => (
                <Type variant="body1" key={line} className={`${marketingType.body} max-w-none`}>
                  {withLinks(line)}
                </Type>
              ))}
          </div>
        </div>
      </MarketingIntakeLayout>
    );
  }

  if (errorReason) {
    // A form that books a meeting sends its visitor back to choose a time,
    // and can point at Calendly. One that books nothing has neither, so it
    // returns to the screen that was sent, with the answers still on it.
    const copy =
      LASTING_ERROR_COPY[errorReason] ??
      (booksAMeeting ? (ERROR_COPY[errorReason] ?? DEFAULT_ERROR) : DEFAULT_ERROR_NO_BOOKING);
    return (
      <MarketingIntakeLayout
        audienceLabel={audienceLabel}
        headline={headline}
        introBody={introBody}
        // The line above the form asks for a choice of time, and there is
        // none to make here.
        mobileView="form"
        homeHref={homeHref}
        primaryLabel={booksAMeeting ? "Choose Another Time" : "Try Again"}
        onPrimary={() => {
          if (booksAMeeting) {
            setIndex(0);
            setSelectedDate(undefined);
          }
          // Choosing a time, or none, is what clears the failure.
          onSelectTime?.("");
        }}
        className={className}
      >
        <div className="flex flex-col gap-4" data-testid="intake-error">
          <Type variant="h3" as="h2">
            {copy.heading}
          </Type>
          <Type variant="body1">{copy.body}</Type>
          {booksAMeeting && calendlyButton}
        </div>
      </MarketingIntakeLayout>
    );
  }

  // After a refused Continue, focus goes to the first thing to fix, in the
  // order the screen shows it, so a keyboard or screen reader is taken to it
  // rather than left on the button. Labels carry the error state too, and
  // are passed over. It waits a moment for the errors to be drawn.
  const focusFirstRefused = (selector: string) =>
    window.setTimeout(() => {
      const refused = screenRef.current?.querySelector<HTMLElement>(selector);
      const target = refused?.matches(FOCUSABLE)
        ? refused
        : refused?.querySelector<HTMLElement>(FOCUSABLE);
      target?.focus();
    }, 0);

  // Enter in a single-line field does what Continue does, as it does in any
  // form. Not in a text area, where it starts a new line, nor in a select,
  // where it chooses the option, nor on a button, which it presses.
  const continueOnEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = event.target;
    if (
      event.key !== "Enter" ||
      event.shiftKey ||
      event.nativeEvent.isComposing ||
      !(target instanceof HTMLInputElement) ||
      target.getAttribute("role") === "combobox" ||
      ["checkbox", "radio", "button", "submit"].includes(target.type)
    ) {
      return;
    }
    event.preventDefault();
    void advance();
  };

  // Pressing the button on an unfinished screen is how somebody asks what is
  // missing, so it answers: the fields show their errors, and a booking step
  // says a choice is needed. It never moves on.
  const advance = async () => {
    // The button that moves on becomes the button that sends, in the same
    // place, when the last screen is reached. A second press that follows the
    // first at once is the end of a double press on Continue, not a decision
    // to send a screen nobody has yet read, and on two of the forms sending
    // books a meeting.
    if (isLast && index > 0 && Date.now() - arrivedAt.current < sendGuardMs) return;
    if (!isChoiceMade) {
      setIsChoiceMissing(true);
      track.problem("invalid_answers", { stepKind: step.kind, fields: [step.kind] });
      focusFirstRefused('[role="radio"]');
      return;
    }
    if (stepFieldNames.length > 0) {
      // A field shows its error once it has been touched or the form has
      // been submitted. Moving between screens submits nothing, so the
      // fields on this one are marked as touched, which rewriting each with
      // its own value does without changing it.
      stepFieldNames.forEach((name) =>
        form.setValue(name as never, form.getValues(name as never), {
          shouldTouch: true,
        })
      );
      const valid = await form.trigger(stepFieldNames as never);
      if (!valid) {
        // Which fields were refused, by name, so a question that turns people
        // away can be found. What was typed into them is never sent.
        track.problem("invalid_answers", {
          stepKind: step.kind,
          fields: stepFieldNames.filter(
            (name) => form.getFieldState(name as never).invalid
          ),
        });
        focusFirstRefused('[aria-invalid="true"]:not(label)');
        return;
      }
    }
    if (isLast) {
      await handleSubmit();
      return;
    }
    goToStep(index + 1);
  };

  const goToStep = (next: number) => {
    setIsChoiceMissing(false);
    hasMoved.current = true;
    arrivedAt.current = Date.now();
    if (next < index) {
      // Back is the browser's Back, so the two never disagree about which
      // screen is showing; the history entry says where to go.
      window.history.back();
      return;
    }
    window.history.pushState({ intake: { step: next } }, "");
    furthest.current = Math.max(furthest.current, next);
    setIndex(next);
  };

  // The shared select does not report what is typed into it, only what is
  // chosen, and the organization list is too long to hand over whole. Typing
  // still raises an ordinary change event from the input inside it, which is
  // caught here on its way up.
  const reportOrganizationSearch = (event: React.FormEvent<HTMLDivElement>) => {
    const target = event.target;
    if (target instanceof HTMLInputElement && target.id === ORGANIZATION_INPUT_ID) {
      onOrganizationSearch?.(target.value);
    }
  };

  const renderStep = () => {
    if (isLoadingTimes && (step.kind === "day" || step.kind === "sessions")) {
      return (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      );
    }

    if (bookingUnavailable && step.kind !== "contact" && step.kind !== "questions") {
      return (
        <div className="flex flex-col gap-4" data-testid="booking-unavailable">
          <Type variant="h4">We cannot show you times right now.</Type>
          <Type variant="body2" className="text-brand-700">
            Please book directly through Calendly and we will see you there.
          </Type>
          {calendlyButton}
        </div>
      );
    }

    if (step.kind === "day" || step.kind === "sessions") {
      // The soonest few, as the design lists them. Everything further out is
      // still reachable through Calendly's own page.
      const options = (
        step.kind === "day"
          ? (days ?? []).map((d) => ({ value: d.date, label: d.label }))
          : (days ?? []).flatMap((d) =>
              d.slots.map((slot: BookingSlot) => ({
                value: slot.start_time,
                label: `${formatDay(slot.start_time)} at ${formatTime(slot.start_time)}`,
              }))
            )
      ).slice(0, BOOKING_OPTIONS_SHOWN);

      // Nothing open to book, as when sessions are yet to be scheduled. A
      // meeting form only takes a submission with a time, so rather than
      // leave the visitor at an empty calendar it points them to where they
      // will hear of new dates, or can write to us meanwhile.
      if (options.length === 0) {
        return (
          <div className="flex flex-col gap-4" data-testid="booking-nothing-open">
            <Type variant="h4" as="p" className={`${marketingType.body} font-bold max-w-none`}>
              No sessions are open yet.
            </Type>
            <Type variant="body2" className={`${marketingType.body} max-w-none`}>
              New dates are being added. In the meantime, sign up for our newsletter, or write
              to us and we&apos;ll be in touch.
            </Type>
            <div className="flex flex-col gap-3 lg:flex-row">
              <Button
                as="a"
                href="/#newsletter"
                variant="primary"
                className={marketingButton.className}
                typeClassName={marketingButton.typeClassName}
                data-testid="booking-nothing-open-updates"
              >
                Get Updates
              </Button>
              <Button
                as="a"
                href="/contact"
                variant="secondary"
                className={marketingButton.className}
                typeClassName={marketingButton.typeClassName}
                data-testid="booking-nothing-open-contact"
              >
                Contact us
              </Button>
            </div>
          </div>
        );
      }

      const chosen = step.kind === "day" ? selectedDate : selectedTime;
      return (
        <div className="flex flex-col gap-3" role="radiogroup" aria-label={step.label}>
          <Type variant="caption" as="p" className="font-medium text-brand-700 max-w-none">
            {step.label}
          </Type>
          {options.map((option) => (
            <Button
              key={option.value}
              variant="secondary"
              role="radio"
              aria-checked={chosen === option.value}
              className={optionRowClass(chosen === option.value)}
              typeClassName={OPTION_TYPE}
              onClick={() => {
                setIsChoiceMissing(false);
                markStarted();
                if (step.kind === "day") {
                  // A time belongs to the day it was chosen on. Changing the
                  // day lets go of it, or the meeting would be booked on the
                  // day the visitor has just left.
                  if (option.value !== selectedDate) onSelectTime?.("");
                  setSelectedDate(option.value);
                } else onSelectTime?.(option.value);
              }}
              data-testid={`intake-option-${option.value}`}
            >
              <RadioMark selected={chosen === option.value} />
              {option.label}
            </Button>
          ))}
          {isChoiceMissing && <FieldError message={CHOICE_REQUIRED[step.kind]} />}
        </div>
      );
    }

    if (step.kind === "time") {
      const day = (days ?? []).find((d) => d.date === selectedDate);
      return (
        <div className="flex flex-col gap-3" role="radiogroup" aria-label="Choose a time">
          <Type variant="caption" as="p" className="font-medium text-brand-700 max-w-none">
            Choose a time
            {day?.slots[0] && (
              <>
                {" "}on{" "}
                <span className="font-bold">{formatDay(day.slots[0].start_time)}</span>
              </>
            )}
          </Type>
          {(day?.slots ?? []).map((slot) => (
            <Button
              key={slot.start_time}
              variant="secondary"
              role="radio"
              aria-checked={selectedTime === slot.start_time}
              className={optionRowClass(selectedTime === slot.start_time)}
              typeClassName={OPTION_TYPE}
              onClick={() => {
                setIsChoiceMissing(false);
                markStarted();
                onSelectTime?.(slot.start_time);
              }}
              data-testid={`intake-option-${slot.start_time}`}
            >
              <RadioMark selected={selectedTime === slot.start_time} />
              {formatTime(slot.start_time)}
            </Button>
          ))}
          {isChoiceMissing && <FieldError message={CHOICE_REQUIRED.time} />}
        </div>
      );
    }

    if (step.kind === "contact") {
      // A question asked on this screen goes where the design puts it: under
      // the email confirmation, ahead of organization and role.
      const drawn = drawnQuestions(step.extra, form.watch("answers"));
      const waiting = new Set(drawn.filter((d) => d.isWaiting).map((d) => d.question.name));
      const asked = drawn.map((d) => d.question);
      const urlQuestions = asked.filter((field) => field.kind === "url");
      const otherQuestions = asked.filter((field) => field.kind !== "url");
      const question = (field: IntakeQuestion) => (
        <PublicQuizQuestion
          key={field.name}
          question={field}
          name={`answers.${field.name}`}
          disabled={waiting.has(field.name)}
          className={field.kind === "textarea" ? QUESTION_BOX : QUESTION_FIELD}
        />
      );
      const hasOrganization = step.fields.includes("organization_name");
      const hasRole = step.fields.includes("self_reported_role");
      const hasMessage = step.fields.includes("message");
      // One form asks for an organization by name and another asks a whole
      // question in its place; a question is given room to be answered in.
      const organizationIsQuestion =
        (fieldLabels?.organization_name ?? "").trim().endsWith("?");

      return (
        <div className={marketingFields.column}>
          <Form
            form={form}
            innerClassName={`justify-start ${
              organizationIsQuestion ? FIELD_GROUP_GAP_CLOSE : FIELD_GROUP_GAP
            }`}
          >
            {(step.fields.includes("first_name") || step.fields.includes("last_name")) && (
              <div className={FIELD_GROUP}>
                {step.fields.includes("first_name") && (
                  <FormField
                    name="firstName"
                    label={labelFor("first_name", "First Name")}
                    component="Input"
                    placeholder={placeholderFor("first_name", "Enter your first name")}
                    required={isRequired("first_name")}
                    className={FIELD}
                  />
                )}
                {step.fields.includes("last_name") && (
                  <FormField
                    name="lastName"
                    label={labelFor("last_name", "Last Name")}
                    component="Input"
                    placeholder={placeholderFor("last_name", "Enter your last name")}
                    required={isRequired("last_name")}
                    className={FIELD}
                  />
                )}
              </div>
            )}
            {step.asksEmail && (
              <div className={FIELD_GROUP}>
                <FormField
                  name="email"
                  label="Email"
                  component="Input"
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                  className={FIELD}
                />
                <FormField
                  name="confirmEmail"
                  label="Email Confirmation"
                  component="Input"
                  placeholder="Enter your email again"
                  required
                  className={FIELD}
                />
              </div>
            )}
            {step.fields.includes("phone_number") && (
              <div className={FIELD_GROUP}>
                <FormField
                  name="phoneNumber"
                  label={labelFor("phone_number", "Phone Number")}
                  component="PhoneInput"
                  required={isRequired("phone_number")}
                  className={FIELD}
                />
                <FormField
                  name="confirmPhoneNumber"
                  label="Phone Number Confirmation"
                  component="PhoneInput"
                  required={isRequired("phone_number")}
                  className={FIELD}
                />
              </div>
            )}
            {urlQuestions.map(question)}
            {(hasOrganization || hasRole || hasMessage || otherQuestions.length > 0) && (
              <div className={FIELD_GROUP}>
                {step.affiliation ? (
                  // The organization and role selects the profile uses, as
                  // one field holding one pair.
                  <div onChange={reportOrganizationSearch}>
                    <FormField
                      name="affiliations"
                      component="AffiliationList"
                      maxItems={1}
                      organizationOptions={organizationOptions ?? []}
                      roleOptions={roleOptions ?? []}
                      isLoading={!!isLoadingRoles}
                      required={
                        isRequired("organization_name") || isRequired("self_reported_role")
                      }
                      className={AFFILIATION_FIELD}
                    />
                  </div>
                ) : (
                  (hasOrganization || hasRole) && (
                    <div
                      className={`grid ${marketingFields.gap} ${
                        hasOrganization && hasRole && !organizationIsQuestion
                          ? "grid-cols-2"
                          : "grid-cols-1"
                      }`}
                    >
                      {hasOrganization && (
                        <FormField
                          name="organizationName"
                          label={labelFor("organization_name", "Organization")}
                          component={organizationIsQuestion ? "Textarea" : "Input"}
                          placeholder={
                            organizationIsQuestion
                              ? "Enter your affiliations"
                              : "Select or type organization"
                          }
                          required={isRequired("organization_name")}
                          className={organizationIsQuestion ? QUESTION_BOX : FIELD}
                        />
                      )}
                      {hasRole && (
                        <FormField
                          name="selfReportedRole"
                          label={labelFor("self_reported_role", "Role")}
                          component="Input"
                          placeholder="Select role"
                          required={isRequired("self_reported_role")}
                          className={FIELD}
                        />
                      )}
                    </div>
                  )
                )}
                {hasMessage && (
                  <FormField
                    name="message"
                    label={labelFor("message", "Message")}
                    component="Textarea"
                    placeholder={placeholderFor("message", "Enter your message")}
                    required={isRequired("message")}
                    className={`${marketingFields.noMark} gap-3`}
                  />
                )}
                {otherQuestions.map(question)}
              </div>
            )}
          </Form>
        </div>
      );
    }

    return (
      <div
        className={`${marketingFields.column} ${QUESTION_SCREEN} ${
          step.fields.length >= WIDE_SCREEN_QUESTIONS ? WIDE_QUESTION_SCREEN : ""
        }`}
      >
        <Form form={form} innerClassName={`justify-start ${marketingFields.gap}`}>
          {/* A question may wait on the answer to another, on this screen
              or an earlier one. */}
          {drawnQuestions(questions, form.watch("answers"))
            .filter(({ question: field }) => step.fields.includes(field))
            .map(({ question: field, isWaiting }) => (
            <PublicQuizQuestion
              key={field.name}
              question={field}
              // Drawn but not yet asked: it waits on another answer.
              disabled={isWaiting}
              name={`answers.${field.name}`}
              // The design sets a pick-several question and its instruction as
              // one line of label, and gives no other question a hint at all.
              label={
                field.kind === "multiselect" && field.help_text
                  ? `${field.label} ${field.help_text}`
                  : field.label
              }
              className={questionClassFor(field)}
            />
          ))}
        </Form>
      </div>
    );
  };

  return (
    <MarketingIntakeLayout
      audienceLabel={audienceLabel}
      headline={headline}
      introBody={introBody}
      // The line above the form asks for a choice of time, so it is shown
      // only over a screen that offers one: not over the contact details and
      // questions after it, nor over a booking screen with nothing to offer.
      stepIntro={isBookingStep && !bookingProblem && !isLoadingTimes ? stepIntro : undefined}
      stepNumber={index + 1}
      stepCount={steps.length}
      primaryLabel={isLast ? finalLabel : "Continue"}
      onPrimary={advance}
      onBack={index > 0 ? () => goToStep(index - 1) : undefined}
      // The first screen has nothing behind it but the homepage, or on a
      // phone the intro, which is a history entry of its own.
      cancelHref={homeHref}
      onMobileBack={() => window.history.back()}
      homeHref={homeHref}
      isPrimaryDisabled={!isChoiceMade || hasStepIssue}
      // A booking screen with nothing to choose offers Calendly instead, and
      // a Continue there could never do anything.
      isPrimaryHidden={!!bookingProblem}
      isSubmitting={form.formState.isSubmitting}
      mobileView={isIntroShown ? "intro" : "form"}
      onMobileContinue={leaveIntro}
      className={className}
    >
      <div
        ref={screenRef}
        tabIndex={-1}
        className="w-full outline-none"
        onKeyDown={continueOnEnter}
      >
        {renderStep()}
      </div>
    </MarketingIntakeLayout>
  );
}

export default MarketingIntakeWizard;
